import { useMemo, useState, useCallback } from 'react';
import { sortRows } from '../utils';

/**
 * Hook to manage sortable columns in a table
 * @param {Array} dataSource - Table data
 * @param {Array} columns - Column configurations
 * @param {Object} options - Hook options
 * @param {boolean} options.sortable - Global sortable flag
 * @param {Object} options.defaultSortState - Initial sort state { key, order }
 * @param {Function} options.onSortChange - Callback when sort changes
 * @returns {Object} - { sortedData, sortState, handleSort(columnKey,'asc'|'desc'), isColumnSortable }
 */
export const useSortableColumns = (dataSource, columns, options = {}) => {
  const { sortable = false, defaultSortState = null, onSortChange } = options;
  const [sortState, setSortState] = useState(defaultSortState);

  const handleSort = useCallback(
    (columnKey, direction) => {
      const column = columns.find(col => col.dataIndex === columnKey || col.key === columnKey);
      if (!column?.sortable && !sortable) return;
      if (direction !== 'asc' && direction !== 'desc') return;

      setSortState(prev => {
        let newState;
        if (prev?.key !== columnKey) {
          newState = { key: columnKey, order: direction };
        } else if (direction === 'asc') {
          newState = prev.order === 'asc' ? null : { key: columnKey, order: 'asc' };
        } else {
          newState = prev.order === 'desc' ? null : { key: columnKey, order: 'desc' };
        }
        onSortChange?.(newState);
        return newState;
      });
    },
    [columns, sortable, onSortChange],
  );

  const sortedData = useMemo(
    () => sortRows(dataSource, columns, sortState),
    [dataSource, sortState, columns],
  );

  const isColumnSortable = useCallback(
    column => {
      const hasSortConfig = !!column.sortConfig;
      return column.sortable !== false && (column.sortable || sortable) && hasSortConfig;
    },
    [sortable],
  );

  return {
    sortedData,
    sortState,
    handleSort,
    isColumnSortable,
  };
};

export default useSortableColumns;
